import React, { useState, useEffect, useRef } from 'react';
import './MobileFilterSheet.css';

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface MobileFilterSheetProps {
  isOpen: boolean;
  onClose: () => void;
  filters: {
    search: string;
    countries: string[];
    industries: string[];
    jobTypes: string[];
    positions: string[];
    salary: [number, number];
  };
  onFilterChange: (filters: any) => void;
  availableOptions: {
    countries: FilterOption[];
    industries: FilterOption[];
    jobTypes: FilterOption[];
    positions: FilterOption[];
  };
}

const MobileFilterSheet: React.FC<MobileFilterSheetProps> = ({
  isOpen,
  onClose,
  filters,
  onFilterChange,
  availableOptions
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [activeTab, setActiveTab] = useState<'basic' | 'location' | 'type' | 'salary'>('basic');
  const sheetRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const currentY = useRef(0);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Touch handlers for swipe to close
  const handleTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;
    
    if (diff > 0 && sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${diff}px)`;
    }
  };

  const handleTouchEnd = () => {
    const diff = currentY.current - startY.current;
    
    if (diff > 100) {
      onClose();
    } else if (sheetRef.current) {
      sheetRef.current.style.transform = 'translateY(0)';
    }
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
    onClose();
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      search: '',
      countries: [],
      industries: [],
      jobTypes: [],
      positions: [],
      salary: [0, 200000] as [number, number]
    };
    setLocalFilters(clearedFilters);
  };

  const updateFilter = (key: string, value: any) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleArrayFilter = (key: string, value: string) => {
    setLocalFilters(prev => {
      const array = prev[key as keyof typeof prev] as string[];
      const newArray = array.includes(value)
        ? array.filter(item => item !== value)
        : [...array, value];
      return { ...prev, [key]: newArray };
    });
  };

  const formatSalary = (value: number) => {
    return `$${value.toLocaleString()}`;
  };

  return (
    <>
      <div 
        className={`mobile-filter-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />
      
      <div 
        ref={sheetRef}
        className={`mobile-filter-sheet ${isOpen ? 'open' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header */}
        <div className="sheet-header">
          <div className="sheet-handle" />
          <h2>Filters</h2>
          <button className="sheet-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${activeTab === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveTab('basic')}
          >
            Basic
          </button>
          <button 
            className={`filter-tab ${activeTab === 'location' ? 'active' : ''}`}
            onClick={() => setActiveTab('location')}
          >
            Location
          </button>
          <button 
            className={`filter-tab ${activeTab === 'type' ? 'active' : ''}`}
            onClick={() => setActiveTab('type')}
          >
            Job Type
          </button>
          <button 
            className={`filter-tab ${activeTab === 'salary' ? 'active' : ''}`}
            onClick={() => setActiveTab('salary')}
          >
            Salary
          </button>
        </div>

        {/* Content */}
        <div className="sheet-content">
          {activeTab === 'basic' && (
            <div className="filter-section">
              <div className="search-section">
                <label>Search</label>
                <input
                  type="search"
                  placeholder="Job title, company, skills..."
                  value={localFilters.search}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  className="filter-search-input"
                />
              </div>

              <div className="filter-group">
                <label>Industries</label>
                <div className="filter-chips">
                  {availableOptions.industries.map(industry => (
                    <button
                      key={industry.value}
                      className={`filter-chip ${localFilters.industries.includes(industry.value) ? 'active' : ''}`}
                      onClick={() => toggleArrayFilter('industries', industry.value)}
                    >
                      {industry.label}
                      {industry.count && <span className="chip-count">{industry.count}</span>}
                    </button>
                  ))}
                </div>
              </div>

              <div className="filter-group">
                <label>Positions</label>
                <div className="filter-chips">
                  {availableOptions.positions.map(position => (
                    <button
                      key={position.value}
                      className={`filter-chip ${localFilters.positions.includes(position.value) ? 'active' : ''}`}
                      onClick={() => toggleArrayFilter('positions', position.value)}
                    >
                      {position.label}
                      {position.count && <span className="chip-count">{position.count}</span>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'location' && (
            <div className="filter-section">
              <div className="filter-group">
                <label>Countries</label>
                <div className="filter-list">
                  {availableOptions.countries.map(country => (
                    <label key={country.value} className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={localFilters.countries.includes(country.value)}
                        onChange={() => toggleArrayFilter('countries', country.value)}
                      />
                      <span className="checkbox-label">
                        {country.label}
                        {country.count && <span className="item-count">({country.count})</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'type' && (
            <div className="filter-section">
              <div className="filter-group">
                <label>Employment Type</label>
                <div className="filter-chips">
                  {availableOptions.jobTypes.map(type => (
                    <button
                      key={type.value}
                      className={`filter-chip ${localFilters.jobTypes.includes(type.value) ? 'active' : ''}`}
                      onClick={() => toggleArrayFilter('jobTypes', type.value)}
                    >
                      {type.label}
                      {type.count && <span className="chip-count">{type.count}</span>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'salary' && (
            <div className="filter-section">
              <div className="filter-group">
                <label>Salary Range</label>
                <div className="salary-range-display">
                  <span>{formatSalary(localFilters.salary[0])}</span>
                  <span>-</span>
                  <span>{formatSalary(localFilters.salary[1])}</span>
                </div>
                
                <div className="salary-inputs">
                  <div className="salary-input-group">
                    <label>Min</label>
                    <input
                      type="number"
                      value={localFilters.salary[0]}
                      onChange={(e) => updateFilter('salary', [parseInt(e.target.value) || 0, localFilters.salary[1]])}
                      step="1000"
                      min="0"
                    />
                  </div>
                  <div className="salary-input-group">
                    <label>Max</label>
                    <input
                      type="number"
                      value={localFilters.salary[1]}
                      onChange={(e) => updateFilter('salary', [localFilters.salary[0], parseInt(e.target.value) || 200000])}
                      step="1000"
                      min="0"
                    />
                  </div>
                </div>

                <input
                  type="range"
                  min="0"
                  max="200000"
                  step="1000"
                  value={localFilters.salary[0]}
                  onChange={(e) => updateFilter('salary', [parseInt(e.target.value), localFilters.salary[1]])}
                  className="salary-slider"
                />
                <input
                  type="range"
                  min="0"
                  max="200000"
                  step="1000"
                  value={localFilters.salary[1]}
                  onChange={(e) => updateFilter('salary', [localFilters.salary[0], parseInt(e.target.value)])}
                  className="salary-slider"
                />
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="sheet-actions">
          <button className="btn-clear" onClick={handleClearFilters}>
            Clear All
          </button>
          <button className="btn-apply" onClick={handleApplyFilters}>
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileFilterSheet;